import {
  Bell,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Link2,
  Mail,
  MessageSquare,
  User,
  Video,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ModuleDrawerAdd } from "@/components/ModuleDrawerAdd";
import { PaneResizer } from "@/components/PaneResizer";
import { HUB_VERSION } from "@/lib/version";
import { cn } from "@/lib/utils";

export const SETTINGS_AREAS: ReadonlyArray<{
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  end?: boolean;
  prefix?: string;
}> = [
  { id: "profile", label: "Profile", path: "/settings/profile", end: true, icon: User },
  {
    id: "connections",
    label: "Connections",
    path: "/settings/connections",
    prefix: "/settings/connections",
    icon: Link2,
  },
  { id: "mail", label: "Mail", path: "/settings/mail", prefix: "/settings/mail", icon: Mail },
  { id: "chat", label: "Chat", path: "/settings/chat", end: true, icon: MessageSquare },
  { id: "calendar", label: "Calendar", path: "/settings/calendar", end: true, icon: CalendarDays },
  { id: "meetings", label: "Meetings", path: "/settings/meetings", end: true, icon: Video },
  {
    id: "notifications",
    label: "Notifications",
    path: "/settings/notifications",
    end: true,
    icon: Bell,
  },
];

const NAV_COLLAPSE_KEY = "atw-hub-settings-nav-collapsed";

function areaIsActive(pathname: string, area: (typeof SETTINGS_AREAS)[number]) {
  if (area.prefix) {
    return pathname.startsWith(area.prefix);
  }
  return pathname === area.path || pathname.startsWith(`${area.path}/`);
}

export function SettingsIndexRedirect() {
  return <Navigate to="/settings/profile" replace />;
}

export function SettingsLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try {
      return localStorage.getItem(NAV_COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    function openDrawer() {
      setDrawerOpen(true);
    }
    window.addEventListener("atw-hub-open-settings-drawer", openDrawer);
    return () => window.removeEventListener("atw-hub-open-settings-drawer", openDrawer);
  }, []);

  function toggleNav() {
    setNavCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(NAV_COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }

  function renderAreaLinks(opts: { onNavigate?: () => void; iconOnly?: boolean }) {
    return SETTINGS_AREAS.map((area) => {
      const Icon = area.icon;
      return (
        <NavLink
          key={area.id}
          to={area.path}
          end={area.end ?? false}
          title={area.label}
          aria-label={area.label}
          className={({ isActive }) => {
            const active =
              isActive || (area.prefix != null && location.pathname.startsWith(area.prefix));
            return cn(
              opts.iconOnly ? "settings-nav-icon-link" : "settings-nav-link",
              active && "active",
            );
          }}
          onClick={() => opts.onNavigate?.()}
        >
          {opts.iconOnly ? (
            <Icon size={18} aria-hidden />
          ) : (
            <>
              <span className="settings-nav-link-icon" aria-hidden>
                <Icon size={16} />
              </span>
              <span className="settings-nav-link-label">{area.label}</span>
            </>
          )}
        </NavLink>
      );
    });
  }

  return (
    <div className={`settings-layout${navCollapsed ? " settings-nav-collapsed" : ""}`}>
      <aside
        className={`settings-nav-pane desktop-only${navCollapsed ? " settings-nav-pane-collapsed" : ""}`}
        aria-label="Settings sections"
      >
        <div className="settings-nav-toggle-row">
          {navCollapsed ? (
            <button
              type="button"
              className="settings-nav-collapse-btn"
              aria-label="Expand settings"
              title="Expand"
              onClick={toggleNav}
            >
              <ChevronRight size={16} />
            </button>
          ) : (
            <>
              <div className="settings-nav-brand">
                <strong>Settings</strong>
                <span className="muted">{HUB_VERSION}</span>
              </div>
              <button
                type="button"
                className="settings-nav-collapse-btn"
                aria-label="Collapse settings"
                title="Collapse"
                onClick={toggleNav}
              >
                <ChevronLeft size={16} />
              </button>
            </>
          )}
        </div>

        {navCollapsed ? (
          <nav className="settings-nav-icon-rail" aria-label="Settings areas">
            {renderAreaLinks({ iconOnly: true })}
          </nav>
        ) : (
          <nav className="settings-nav-list" aria-label="Settings areas">
            {renderAreaLinks({})}
          </nav>
        )}
      </aside>
      {!navCollapsed ? (
        <PaneResizer id="settings.nav" cssVar="--settings-nav-w" min={180} max={380} label="Resize settings menu" />
      ) : null}

      <div className="settings-main" data-pane-main>
        {/* Phone: no section chip — hamburger already opens Settings areas. */}
        <div className="settings-main-head phone-only">
          <span className="muted settings-main-tip">{HUB_VERSION}</span>
        </div>
        <div className="settings-main-body page-pad">
          <Outlet />
        </div>
      </div>

      {drawerOpen ? (
        <div
          className="drawer-scrim phone-only"
          onClick={() => setDrawerOpen(false)}
        >
          <aside
            className="settings-sections-drawer"
            onClick={(e) => e.stopPropagation()}
            aria-label="Settings sections"
          >
            <div className="settings-sections-drawer-head">
              <strong>Settings</strong>
              <button
                type="button"
                className="icon-btn"
                aria-label="Close"
                onClick={() => setDrawerOpen(false)}
              >
                <X size={18} />
              </button>
            </div>
            <nav className="settings-drawer-list" aria-label="Settings areas">
              {SETTINGS_AREAS.map((area) => {
                const Icon = area.icon;
                const active = areaIsActive(location.pathname, area);
                return (
                  <button
                    key={area.id}
                    type="button"
                    className={cn("settings-drawer-link", active && "active")}
                    onClick={() => {
                      setDrawerOpen(false);
                      navigate(area.path);
                    }}
                  >
                    <Icon size={16} aria-hidden />
                    {area.label}
                  </button>
                );
              })}
            </nav>
            <ModuleDrawerAdd
              to="/settings/connections"
              label="Connect account"
              onNavigate={() => setDrawerOpen(false)}
            />
          </aside>
        </div>
      ) : null}
    </div>
  );
}
